/**
 * Progressive 50/50 Style3 (Momentum Meter) — Style-specific sections for GridEngine.
 *
 * hero_section: 2-column layout with MomentumArc SVG gauge, momentum theme, delta tracking
 * jackpot_summary: 8-week trajectory sparkline bar chart
 * final_cta: Gradient CTA matching hero theme
 */
import React, { useMemo } from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import Activity from 'lucide-react/dist/esm/icons/activity.js';
import ArrowUp from 'lucide-react/dist/esm/icons/arrow-up.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import DrawCountdownHeader from '../../../components/DrawCountdownHeader'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'

/**
 * MomentumArc SVG Component
 */
const MomentumArc = ({ pct }) => {
  const R = 140
  const CX = 160
  const CY = 160
  const circumference = Math.PI * R
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circumference

  return (
    <svg viewBox="0 0 320 190" className="w-full max-w-sm mx-auto" aria-hidden="true">
      <defs>
        <linearGradient id="momentum-arc-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="var(--charity-gradient-start)" />
          <stop offset="100%" stopColor="var(--charity-gradient-end)" />
        </linearGradient>
      </defs>
      {/* Track */}
      <path
        d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
        fill="none"
        stroke="rgba(255,255,255,0.22)"
        strokeWidth="22"
        strokeLinecap="round"
      />
      {/* Fill */}
      <path
        d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
        fill="none"
        stroke="url(#momentum-arc-grad)"
        strokeWidth="22"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        style={{ transition: 'stroke-dasharray 800ms ease-out' }}
      />
      {/* Needle */}
      {(() => {
        const angle = Math.PI - (Math.max(0, Math.min(100, pct)) / 100) * Math.PI
        const nx = CX + Math.cos(angle) * (R - 10)
        const ny = CY - Math.sin(angle) * (R - 10)
        return (
          <>
            <line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#fff" strokeWidth="4" strokeLinecap="round" />
            <circle cx={CX} cy={CY} r="8" fill="#fff" />
          </>
        )
      })()}
    </svg>
  )
}

/**
 * Progressive Style3 Hero Section
 */
export function ProgressiveStyle3HeroSection({
  mainDraw,
  t,
  c,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  timeLeft,
  shouldAnimate,
  countdownStyle,
}) {
  const { c: useC } = useContent()
  const ct = c || useC
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const lastWeekAmount = mainDraw?.previous_jackpot ?? mainDraw?.last_week_jackpot ?? Math.max(1, Math.round(jackpotAmount * 0.82))
  const ticketsSold = mainDraw?.total_tickets_sold
  const ticketPrice = mainDraw?.ticket_price ?? 5
  const rolloverStreak = mainDraw?.rollover_streak || 0
  const growthPerHour = mainDraw?.growth_per_hour ?? Math.max(1, Math.round((jackpotAmount - lastWeekAmount) / 168))

  const deltaAbs = Math.max(0, jackpotAmount - lastWeekAmount)
  const deltaPct = lastWeekAmount > 0 ? (deltaAbs / lastWeekAmount) * 100 : 0
  const meterFillPct = useMemo(() => {
    const raw = Math.min(100, (deltaPct / 100) * 100)
    return Math.max(deltaAbs > 0 ? 8 : 0, Math.min(100, raw))
  }, [deltaPct, deltaAbs])

  const heroGradientStyle = {
    backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
    color: '#fff',
  }

  return (
    <section className="relative overflow-hidden" style={heroGradientStyle}>
      <div className="absolute inset-0 bg-black/10 pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <HeroSiteLogo tone="dark" />
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* LEFT — Copy + CTA */}
          <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6 backdrop-blur-sm"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                color: '#fff',
              }}
            >
              <Activity className="w-3.5 h-3.5" />
              <InlineTranslationEdit
                translationKey="progMomentum.badge"
                value={ct('progMomentum.badge', t?.('progMomentum.badge'))}
                fallback="Live Momentum"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight" style={{ color: '#fff' }}>
              <InlineTranslationEdit
                translationKey="progMomentum.title"
                value={ct('progMomentum.title', t?.('progMomentum.title'))}
                fallback="The jackpot is moving."
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>

            <p className="text-lg md:text-xl mb-8 max-w-xl opacity-90" style={{ color: '#fff' }}>
              <InlineTranslationEdit
                translationKey="progMomentum.subtitle"
                value={ct('progMomentum.subtitle', t?.('progMomentum.subtitle'))}
                fallback="Watch the meter climb in real time. Every ticket pushes the needle further."
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>

            <button
              onClick={() => purchaseNavigate('/purchase')}
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-xl font-black rounded-2xl shadow-2xl transition-all hover:scale-105"
              style={{ color: 'var(--charity-primary)' }}
            >
              <Ticket className="w-6 h-6" />
              <InlineTranslationEdit
                translationKey="progMomentum.cta"
                value={ct('progMomentum.cta', t?.('progMomentum.cta'))}
                fallback="GET YOUR TICKETS"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </button>

            <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-6 text-sm opacity-90" style={{ color: '#fff' }}>
              {ticketsSold != null && (
                <div className="flex items-center gap-2">
                  <strong>{ticketsSold.toLocaleString()}</strong>
                  <span>
                    <InlineTranslationEdit
                      translationKey="progMomentum.ticketsSold"
                      value={ct('progMomentum.ticketsSold', t?.('progMomentum.ticketsSold'))}
                      fallback="tickets sold"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                <strong>${ticketPrice}</strong>
                <span>
                  <InlineTranslationEdit
                    translationKey="progMomentum.perTicket"
                    value={ct('progMomentum.perTicket', t?.('progMomentum.perTicket'))}
                    fallback="per ticket"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </span>
              </div>
              {rolloverStreak > 0 && (
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <strong>{rolloverStreak}</strong>
                  <span>
                    <InlineTranslationEdit
                      translationKey="progMomentum.rolloverStreak"
                      value={ct('progMomentum.rolloverStreak', t?.('progMomentum.rolloverStreak'))}
                      fallback="week rollover streak"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Momentum Meter card */}
          <div className="flex justify-center lg:justify-end">
            <div
              className="relative w-full max-w-md rounded-3xl p-6 sm:p-8 backdrop-blur-md border"
              style={{
                backgroundColor: 'rgba(255,255,255,0.10)',
                borderColor: 'rgba(255,255,255,0.30)',
              }}
            >
              <div className="text-center mb-2">
                <p className="text-xs font-bold uppercase tracking-widest opacity-80" style={{ color: '#fff' }}>
                  <InlineTranslationEdit
                    translationKey="progMomentum.currentJackpot"
                    value={ct('progMomentum.currentJackpot', t?.('progMomentum.currentJackpot'))}
                    fallback="Current Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
              </div>

              <MomentumArc pct={meterFillPct} />

              <div className="-mt-6 text-center">
                <p className="text-4xl sm:text-5xl font-black" style={{ color: '#fff' }}>
                  ${jackpotAmount.toLocaleString()}
                </p>

                {/* Delta badge vs last week */}
                <div
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.22)',
                    color: '#fff',
                  }}
                >
                  <ArrowUp className="w-4 h-4" />
                  <span>+${deltaAbs.toLocaleString()}</span>
                  <span className="opacity-80">· +{deltaPct.toFixed(1)}%</span>
                  <span className="opacity-70 font-normal">
                    <InlineTranslationEdit
                      translationKey="progMomentum.vsLastWeek"
                      value={ct('progMomentum.vsLastWeek', t?.('progMomentum.vsLastWeek'))}
                      fallback="vs last week"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>

                {/* Growing-by ticker */}
                <div className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold opacity-90" style={{ color: '#fff' }}>
                  <TrendingUp className="w-4 h-4" />
                  <span>
                    <InlineTranslationEdit
                      translationKey="progMomentum.growingBy"
                      value={ct('progMomentum.growingBy', t?.('progMomentum.growingBy'))}
                      fallback="Growing by"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />{' '}
                    <strong>${growthPerHour.toLocaleString()}/hr</strong>
                  </span>
                </div>
              </div>

              {/* Countdown */}
              {mainDraw?.draw_date && (
                <div
                  className="mt-6 rounded-2xl p-4 border"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    borderColor: 'rgba(255,255,255,0.28)',
                  }}
                >
                  <DrawCountdownHeader draw={mainDraw} className="mb-2" labelClassName="text-white/80">
                    <InlineTranslationEdit
                      translationKey="progMomentum.drawIn"
                      value={ct('progMomentum.drawIn', t?.('progMomentum.drawIn'))}
                      fallback="Draw In"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </DrawCountdownHeader>
                  <CountdownTimerStyles timeLeft={timeLeft} shouldAnimate={shouldAnimate} style={countdownStyle} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Progressive Style3 Jackpot Summary Section — 8-week sparkline
 */
export function ProgressiveStyle3JackpotSummarySection({ mainDraw, t, c, editMode, handleTranslationUpdate }) {
  const { c: useC } = useContent()
  const ct = c || useC

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const currentWeek = mainDraw?.current_week || null
  const history = mainDraw?.jackpot_history

  const sparkData = useMemo(() => {
    if (Array.isArray(history) && history.length > 0) {
      return history.slice(-8).map(h => ({ week: h.week ?? h.draw_number ?? 0, amount: h.amount ?? h.jackpot ?? 0 }))
    }
    // Synthesize 8 weeks
    const weeks = []
    let amt = Math.max(100, Math.round(jackpotAmount * 0.35))
    const step = Math.max(1, Math.round((jackpotAmount - amt) / 7))
    for (let i = 0; i < 7; i++) {
      weeks.push({ week: (currentWeek || 8) - (7 - i), amount: amt })
      amt += step
    }
    weeks.push({ week: currentWeek || 8, amount: jackpotAmount, isCurrent: true })
    return weeks
  }, [history, jackpotAmount, currentWeek])

  const sparkMax = Math.max(1, ...sparkData.map(d => d.amount))

  return (
    <section className="py-12 bg-white dark:bg-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="rounded-2xl p-6 sm:p-8 border"
          style={{
            backgroundColor: 'color-mix(in oklab, var(--charity-primary) 6%, transparent)',
            borderColor: 'color-mix(in oklab, var(--charity-primary) 20%, transparent)',
          }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                <TrendingUp className="w-6 h-6" style={{ color: 'var(--charity-primary)' }} />
                <InlineTranslationEdit
                  translationKey="progMomentum.trajectoryTitle"
                  value={ct('progMomentum.trajectoryTitle', t?.('progMomentum.trajectoryTitle'))}
                  fallback="8-Week Trajectory"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <InlineTranslationEdit
                  translationKey="progMomentum.trajectorySubtitle"
                  value={ct('progMomentum.trajectorySubtitle', t?.('progMomentum.trajectorySubtitle'))}
                  fallback="How the jackpot has climbed week over week."
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <InlineTranslationEdit
                  translationKey="progMomentum.totalGrowth"
                  value={ct('progMomentum.totalGrowth', t?.('progMomentum.totalGrowth'))}
                  fallback="Total growth"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
              <p className="text-2xl font-black" style={{ color: 'var(--charity-primary)' }}>
                +${Math.max(0, jackpotAmount - (sparkData[0]?.amount ?? jackpotAmount)).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Bars */}
          <div className="overflow-x-auto -mx-2 px-2 pb-2">
            <div className="flex items-end gap-2 sm:gap-3 min-w-[480px] sm:min-w-0 h-44">
              {sparkData.map((d, i) => {
                const h = (d.amount / sparkMax) * 100
                const isCurrent = d.isCurrent || i === sparkData.length - 1
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                    <span className="text-[10px] sm:text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                      ${d.amount >= 1000 ? `${Math.round(d.amount / 1000)}k` : d.amount}
                    </span>
                    <div
                      className="w-full rounded-t-lg transition-all"
                      style={{
                        height: `${Math.max(6, h)}%`,
                        background: isCurrent
                          ? 'linear-gradient(180deg, var(--charity-gradient-start), var(--charity-gradient-end))'
                          : 'color-mix(in oklab, var(--charity-primary) 45%, transparent)',
                        boxShadow: isCurrent
                          ? '0 0 20px color-mix(in oklab, var(--charity-primary) 45%, transparent)'
                          : 'none',
                      }}
                    />
                    <span className="text-[10px] mt-1 text-gray-500 dark:text-gray-400">W{d.week}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Progressive Style3 Final CTA Section
 */
export function ProgressiveStyle3FinalCTASection({ mainDraw, t, c, editMode, handleTranslationUpdate, onLoginClick }) {
  const { c: useC } = useContent()
  const ct = c || useC
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)

  const heroGradientStyle = {
    backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
    color: '#fff',
  }

  return (
    <section className="py-16 relative overflow-hidden" style={heroGradientStyle}>
      <div className="absolute inset-0 bg-black/10 pointer-events-none" aria-hidden="true" />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ color: '#fff' }}>
          <InlineTranslationEdit
            translationKey="progMomentum.finalCtaTitle"
            value={ct('progMomentum.finalCtaTitle', t?.('progMomentum.finalCtaTitle'))}
            fallback="Push the meter further."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto" style={{ color: '#fff' }}>
          <InlineTranslationEdit
            translationKey="progMomentum.finalCtaDesc"
            value={ct('progMomentum.finalCtaDesc', t?.('progMomentum.finalCtaDesc'))}
            fallback={`Current jackpot: $${jackpotAmount.toLocaleString()}. Every ticket tips the needle.`}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="inline-flex items-center gap-3 px-10 py-5 bg-white text-xl font-black rounded-2xl hover:scale-105 transition-all shadow-2xl"
          style={{ color: 'var(--charity-primary)' }}
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit
            translationKey="progMomentum.finalCtaButton"
            value={ct('progMomentum.finalCtaButton', t?.('progMomentum.finalCtaButton'))}
            fallback="GET YOUR TICKETS NOW"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </section>
  )
}
